import { FormLabel, FormSelect, FormTextarea } from "@/components";
import { Button, Label, RadioGroup, RadioGroupItem, } from "@/components/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { Bookmark } from "lucide-react";
import { FC, useEffect } from "react";
import { Controller, FormProvider, useWatch } from "react-hook-form";
import { useAddEditResource } from "./useAddEditResource";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { LOGIN_ROLES } from "@/lib/utils";

const AddEditResource: FC = () => {
  const {
    form,
    onSubmit,
    isSubmitting,
    regionHeadOptions,
    isLoadingRegionHeads,
    labsteamRecords,
    isLoadingExisting,
    fields,
    handleAddNewRow,
    
  } = useAddEditResource();

  const { handleSubmit, control } = form;
  const { formState } = form;
  const { errors } = formState as any;

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);

  // watch the whole resources array so we can reactively read per-row selections
  const resourcesWatch = useWatch({ control, name: "resources" });

  // Whenever a row's regionHeadId changes, copy the selected profile name into
  // resources.[index].resourceName so it is part of the form values.
  // This keeps the 'Name' display stored in the form as requested.
  useEffect(() => {
    if (!resourcesWatch) return;
    resourcesWatch.forEach((res: any, idx: number) => {
      const selectedId = res?.regionHeadId;
      const existingName = res?.resourceName;
      if (selectedId) {
        const profile = labsteamRecords?.find((p: any) => p.profile_id === selectedId);
        if (profile && profile.name && existingName !== profile.name) {
          form.setValue(`resources.${idx}.resourceName`, profile.name);
        }
      }
    });
  }, [resourcesWatch, labsteamRecords, form]);

  return (
    <FormProvider {...form}>
      {isLoadingExisting ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading existing data...</div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(
          onSubmit
          )} className="grid gap-4 mt-10">
          <div className="flex justify-between items-center mb-6">
  <h2 className="text-[18px] text-primary">Resource Allocation Info</h2>

  { ( hasRole("LABS_TEAM_HEAD") || hasRole("SUPER_ADMIN")) && (
    <Button
      type="submit"
      variant="default" 
      disabled={isSubmitting}
    >
      Save
    </Button>
  )}
</div>

          
                  

          {/* 👇 Repeatable Fields */}
          {fields.map((item, index) => {
  const isDeleted = resourcesWatch?.[index]?.isDeleted;
  if (isDeleted) return null; // hide deleted ones from UI

  return (
    <div key={item.id} className="p-4 mb-4 rounded-lg relative">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormSelect
          name={`resources.${index}.regionHeadId`}
          control={control}
          label="Name"
          placeholder="Please select"
          options={regionHeadOptions}
          isRequired
          isLoading={isLoadingRegionHeads}
          disabled={!hasRole("LABS_TEAM_HEAD") && !hasRole("SUPER_ADMIN")}
        />
        {/* Mobile & Email readonly fields */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            Mobile No.
          </label>
          <input
            type="text"
            placeholder="Autopopulated"
            value={
              labsteamRecords?.find(
                (p: any) => p.profile_id === resourcesWatch?.[index]?.regionHeadId
              )?.mobile_number || ""
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            Email ID
          </label>
          <input
            type="text"
            placeholder="Autopopulated"
            value={
              labsteamRecords?.find(
                (p: any) => p.profile_id === resourcesWatch?.[index]?.regionHeadId
              )?.email || ""
            }
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
          />
        </div>
      </div>

      {/* Trash Icon — mark as deleted */}
      {(hasRole("LABS_TEAM_HEAD") || hasRole("SUPER_ADMIN")) && (
      <button
        type="button"
        aria-label="Remove row"
        onClick={() => form.setValue(`resources.${index}.isDeleted`, true)}
        className="absolute -right-1 top-14 text-red-600 hover:text-red-800 cursor-pointer"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
      )}

    </div>
  );
})}


          {/* Add Button */}
{(hasRole("LABS_TEAM_HEAD") || hasRole("SUPER_ADMIN")) && (
  <div className="flex justify-start">
    <Button
      type="button"
      onClick={handleAddNewRow}
      className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90"
    >
      <PlusIcon className="w-4 h-4" />
      Add More
    </Button>
  </div>
)}

        </form>
      )}
    </FormProvider>
  );
};


export default AddEditResource;
